/**
 * De hoogte van het gedeelde venster (`components/common/Modal.jsx`), die de
 * gebruiker zelf kan verslepen.
 *
 * **In localStorage en niet in Firestore.** Dit is een voorkeur van één mens op
 * één scherm, geen instelling van de organisatie: een collega met een laptop
 * hoort niet opgezadeld te worden met de hoogte van iemands 27-inch scherm.
 *
 * AI-CONTEXT: See docs/features/modal-resize.md
 */

/** Eén sleutel voor alle vensters: 'meer werkruimte' is een gewoonte, geen keuze per scherm. */
const MODAL_HEIGHT_KEY = 'deccos.modalHeight'

/** Hieronder is er niets meer om mee te werken en is de voetregel in beeld nog het enige. */
const MIN_MODAL_HEIGHT = 320

/** Deel van de schermhoogte dat een venster hoogstens mag innemen. */
const MAX_MODAL_HEIGHT = 0.92

/**
 * Houdt een hoogte binnen wat op dit scherm werkbaar is.
 *
 * De bovengrens wint van de ondergrens: op een scherm dat kleiner is dan de
 * ondergrens zou het venster er anders onderuit steken, en dan valt de
 * voetregel met 'Opslaan' weg.
 *
 * @param {*} height - Gevraagde hoogte in pixels
 * @param {number} viewportHeight - `window.innerHeight`
 * @returns {number|null} De toegestane hoogte, of null bij onbruikbare invoer
 */
const clampModalHeight = (height, viewportHeight) => {
    if (typeof height !== 'number' || !Number.isFinite(height)) return null

    const max = Math.round(viewportHeight * MAX_MODAL_HEIGHT)
    return Math.min(Math.max(height, MIN_MODAL_HEIGHT), max)
}

/**
 * De bewaarde hoogte, aangepast op het scherm van nu.
 *
 * Elke lees zit in een try/catch: in een privévenster of met geblokkeerde
 * site-gegevens gooit `localStorage`, en een venster dat daardoor niet opengaat
 * is erger dan een venster dat zijn hoogte vergeet.
 *
 * @param {number} viewportHeight - `window.innerHeight`
 * @returns {number|null} De hoogte, of null als er niets bruikbaars staat
 */
const readStoredModalHeight = (viewportHeight) => {
    try {
        const ruw = window.localStorage.getItem(MODAL_HEIGHT_KEY)
        if (ruw === null) return null
        // `Number('')` is 0 en `Number('geen getal')` is NaN; allebei moeten ze
        // hier als 'niets bruikbaars' eindigen en niet als een hoogte.
        const getal = Number(ruw)
        if (!Number.isFinite(getal) || getal <= 0) return null
        return clampModalHeight(getal, viewportHeight)
    } catch (e) {
        return null
    }
}

/**
 * Bewaart de hoogte, al geknipt op wat hier past.
 *
 * @param {number} height - Hoogte in pixels
 * @param {number} viewportHeight - `window.innerHeight`
 * @returns {void}
 */
const storeModalHeight = (height, viewportHeight) => {
    const toegestaan = clampModalHeight(height, viewportHeight)
    if (toegestaan === null) return

    try {
        window.localStorage.setItem(MODAL_HEIGHT_KEY, String(toegestaan))
    } catch (e) {
        // Niets te doen: de hoogte geldt deze sessie wel, hij overleeft alleen
        // het sluiten van het tabblad niet.
    }
}

export {
    MODAL_HEIGHT_KEY,
    MIN_MODAL_HEIGHT,
    MAX_MODAL_HEIGHT,
    clampModalHeight,
    readStoredModalHeight,
    storeModalHeight,
}
